import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { exportOfferToPDF, OfferData } from '@/lib/pdfExport';
import { Plus, Trash2, FileDown, Save } from 'lucide-react';

interface OfferFormData {
  offerNumber: string;
  offerDate: string;
  validUntil: string;
  customerName: string;
  customerCompany: string;
  customerAddress: string;
  customerEmail: string;
  customerPhone: string;
  positions: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
  }>;
  notes: string;
  paymentTerms: string;
  additionalContent: string;
  taxRate: number;
  isKleinunternehmer: boolean;
}

interface OfferGeneratorProps {
  editingOffer?: {
    id: string;
    offer_number: string;
    customer_name: string;
    customer_address: string | null;
    customer_email: string | null;
    offer_date: string;
    valid_until: string | null;
    items: Array<{
      description: string;
      quantity: number;
      unit: string;
      unitPrice: number;
    }>;
    additional_content: string | null;
    notes: string | null;
  } | null;
  onSaved?: () => void;
  onCancel?: () => void;
}

function generateOfferNumber(): string {
  const now = new Date();
  const year = now.getFullYear();
  const random = Math.floor(Math.random() * 9000) + 1000;
  return `KT-${year}-${random}`;
}

function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function OfferGenerator({ editingOffer, onSaved, onCancel }: OfferGeneratorProps) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const today = new Date();
  const defaultValidUntil = addDays(today, 30);

  const { register, control, handleSubmit, watch, reset, formState: { errors } } = useForm<OfferFormData>({
    defaultValues: {
      offerNumber: generateOfferNumber(),
      offerDate: formatDateForInput(today),
      validUntil: formatDateForInput(defaultValidUntil),
      customerName: '',
      customerCompany: '',
      customerAddress: '',
      customerEmail: '',
      customerPhone: '',
      positions: [{ description: '', quantity: 1, unit: 'Stk', unitPrice: 0 }],
      notes: '',
      paymentTerms: 'Zahlbar innerhalb von 14 Tagen ohne Abzug.',
      additionalContent: '',
      taxRate: 19,
      isKleinunternehmer: true,
    },
  });

  // Load editing offer data
  useEffect(() => {
    if (editingOffer) {
      reset({
        offerNumber: editingOffer.offer_number,
        offerDate: editingOffer.offer_date,
        validUntil: editingOffer.valid_until || formatDateForInput(defaultValidUntil),
        customerName: editingOffer.customer_name,
        customerCompany: '',
        customerAddress: editingOffer.customer_address || '',
        customerEmail: editingOffer.customer_email || '',
        customerPhone: '',
        positions: editingOffer.items?.length > 0 ? editingOffer.items : [{ description: '', quantity: 1, unit: 'Stk', unitPrice: 0 }],
        notes: editingOffer.notes || '',
        paymentTerms: 'Zahlbar innerhalb von 14 Tagen ohne Abzug.',
        additionalContent: editingOffer.additional_content || '',
        taxRate: 19,
        isKleinunternehmer: true,
      });
    }
  }, [editingOffer, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'positions',
  });

  const watchPositions = watch('positions');
  const watchTaxRate = watch('taxRate');
  const watchIsKleinunternehmer = watch('isKleinunternehmer');

  const netTotal = watchPositions?.reduce((sum, pos) => {
    return sum + (pos.quantity || 0) * (pos.unitPrice || 0);
  }, 0) || 0;

  const taxAmount = watchIsKleinunternehmer ? 0 : netTotal * (watchTaxRate / 100);
  const grossTotal = netTotal + taxAmount;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const saveOffer = async (data: OfferFormData, andExport: boolean = false) => {
    setIsSaving(true);
    try {
      const offerRecord = {
        offer_number: data.offerNumber,
        customer_name: data.customerName,
        customer_address: data.customerAddress || null,
        customer_email: data.customerEmail || null,
        offer_date: data.offerDate,
        valid_until: data.validUntil || null,
        items: data.positions.filter(p => p.description && p.unitPrice > 0),
        additional_content: data.additionalContent || null,
        notes: data.notes || null,
        status: 'draft',
        total_net: netTotal,
        total_vat: taxAmount,
        total_gross: grossTotal,
      };

      if (editingOffer?.id) {
        const { error } = await supabase
          .from('offers')
          .update(offerRecord)
          .eq('id', editingOffer.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('offers')
          .insert(offerRecord);
        if (error) throw error;
      }

      toast({
        title: 'Gespeichert',
        description: `Angebot ${data.offerNumber} wurde gespeichert.`,
      });

      if (andExport) {
        await handleExport(data);
      }

      onSaved?.();
    } catch (error: any) {
      console.error('Error saving offer:', error);
      toast({
        title: 'Fehler',
        description: 'Angebot konnte nicht gespeichert werden.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async (data: OfferFormData) => {
    setIsGenerating(true);
    try {
      const offerData: OfferData = {
        offerNumber: data.offerNumber,
        offerDate: new Date(data.offerDate),
        validUntil: new Date(data.validUntil),
        customer: {
          name: data.customerName,
          company: data.customerCompany || undefined,
          address: data.customerAddress,
          email: data.customerEmail,
          phone: data.customerPhone || undefined,
        },
        positions: data.positions.filter(p => p.description && p.unitPrice > 0),
        notes: data.notes || undefined,
        paymentTerms: data.paymentTerms || undefined,
        additionalContent: data.additionalContent || undefined,
        taxRate: data.taxRate,
        isKleinunternehmer: data.isKleinunternehmer,
      };

      await exportOfferToPDF(offerData);
      
      toast({
        title: 'PDF erstellt',
        description: `Angebot ${data.offerNumber} wurde heruntergeladen.`,
      });
    } catch (error: any) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Fehler',
        description: 'PDF konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const onSubmit = async (data: OfferFormData) => {
    await saveOffer(data, true);
  };

  return (
    <Card>
      <CardHeader className="border-b bg-slate-50">
        <CardTitle className="text-xl flex items-center gap-2">
          <FileDown className="w-5 h-5" />
          Angebots-Generator
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Offer Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="offerNumber">Angebotsnummer</Label>
              <Input
                id="offerNumber"
                {...register('offerNumber', { required: 'Angebotsnummer erforderlich' })}
                placeholder="KT-2026-0001"
              />
              {errors.offerNumber && (
                <p className="text-sm text-red-500">{errors.offerNumber.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="offerDate">Angebotsdatum</Label>
              <Input
                id="offerDate"
                type="date"
                {...register('offerDate', { required: 'Datum erforderlich' })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validUntil">Gültig bis</Label>
              <Input
                id="validUntil"
                type="date"
                {...register('validUntil', { required: 'Gültigkeitsdatum erforderlich' })}
              />
            </div>
          </div>

          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-700 border-b pb-2">Kundeninformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Name *</Label>
                <Input
                  id="customerName"
                  {...register('customerName', { required: 'Name erforderlich' })}
                  placeholder="Max Mustermann"
                />
                {errors.customerName && (
                  <p className="text-sm text-red-500">{errors.customerName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerCompany">Firma</Label>
                <Input
                  id="customerCompany"
                  {...register('customerCompany')}
                  placeholder="Musterfirma GmbH"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="customerAddress">Adresse *</Label>
                <Textarea
                  id="customerAddress"
                  {...register('customerAddress', { required: 'Adresse erforderlich' })}
                  placeholder="Musterstraße 1&#10;12345 Musterstadt"
                  rows={2}
                />
                {errors.customerAddress && (
                  <p className="text-sm text-red-500">{errors.customerAddress.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerEmail">E-Mail *</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  {...register('customerEmail', { 
                    required: 'E-Mail erforderlich',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Ungültige E-Mail-Adresse',
                    },
                  })}
                  placeholder="kunde@email.de"
                />
                {errors.customerEmail && (
                  <p className="text-sm text-red-500">{errors.customerEmail.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerPhone">Telefon</Label>
                <Input
                  id="customerPhone"
                  {...register('customerPhone')}
                  placeholder="+49 123 456789"
                />
              </div>
            </div>
          </div>

          {/* Positions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold text-slate-700">Leistungspositionen</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ description: '', quantity: 1, unit: 'Stk', unitPrice: 0 })}
              >
                <Plus className="w-4 h-4 mr-2" />
                Position hinzufügen
              </Button>
            </div>

            <div className="space-y-3">
              {/* Header */}
              <div className="hidden md:grid grid-cols-12 gap-2 text-sm font-medium text-slate-600 px-2">
                <div className="col-span-1">Pos</div>
                <div className="col-span-5">Beschreibung</div>
                <div className="col-span-1">Menge</div>
                <div className="col-span-1">Einheit</div>
                <div className="col-span-2">Einzelpreis</div>
                <div className="col-span-1">Gesamt</div>
                <div className="col-span-1"></div>
              </div>

              {fields.map((field, index) => {
                const quantity = watchPositions?.[index]?.quantity || 0;
                const unitPrice = watchPositions?.[index]?.unitPrice || 0;
                const lineTotal = quantity * unitPrice;

                return (
                  <div key={field.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg">
                    <div className="md:col-span-1 flex items-center">
                      <span className="text-sm font-medium text-slate-500">{index + 1}</span>
                    </div>
                    <div className="md:col-span-5">
                      <Input
                        {...register(`positions.${index}.description` as const, { required: true })}
                        placeholder="Beschreibung der Leistung"
                        className="bg-white"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        {...register(`positions.${index}.quantity` as const, { valueAsNumber: true })}
                        placeholder="1"
                        className="bg-white"
                      />
                    </div>
                    <div className="md:col-span-1">
                      <Input
                        {...register(`positions.${index}.unit` as const)}
                        placeholder="Stk"
                        className="bg-white"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        {...register(`positions.${index}.unitPrice` as const, { valueAsNumber: true })}
                        placeholder="0.00"
                        className="bg-white"
                      />
                    </div>
                    <div className="md:col-span-1 flex items-center">
                      <span className="text-sm font-medium">{formatCurrency(lineTotal)}</span>
                    </div>
                    <div className="md:col-span-1 flex items-center justify-end">
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="flex justify-end mt-4">
              <div className="w-full md:w-96 space-y-3 p-4 bg-slate-100 rounded-lg">
                {/* Kleinunternehmer Toggle */}
                <div className="flex items-center justify-between pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isKleinunternehmer"
                      {...register('isKleinunternehmer')}
                      className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <Label htmlFor="isKleinunternehmer" className="text-sm font-medium cursor-pointer">
                      Kleinunternehmer (§19 UStG)
                    </Label>
                  </div>
                </div>

                <div className="flex justify-between text-sm">
                  <span>Netto:</span>
                  <span className="font-medium">{formatCurrency(netTotal)}</span>
                </div>
                
                {!watchIsKleinunternehmer && (
                  <div className="flex justify-between text-sm items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span>MwSt.</span>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        {...register('taxRate', { valueAsNumber: true })}
                        className="w-16 h-7 text-center bg-white"
                      />
                      <span>%:</span>
                    </div>
                    <span className="font-medium">{formatCurrency(taxAmount)}</span>
                  </div>
                )}
                
                {watchIsKleinunternehmer && (
                  <p className="text-xs text-muted-foreground italic">
                    Keine MwSt. gemäß §19 UStG (Kleinunternehmerregelung)
                  </p>
                )}
                
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Gesamt:</span>
                  <span className="text-cyan-600">{formatCurrency(grossTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Terms & Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="paymentTerms">Zahlungsbedingungen</Label>
              <Textarea
                id="paymentTerms"
                {...register('paymentTerms')}
                placeholder="Zahlbar innerhalb von 14 Tagen ohne Abzug."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Hinweise / Notizen</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder="Anfahrtskosten im Umkreis von 20km inklusive."
                rows={3}
              />
            </div>
          </div>

          {/* Additional HTML Content */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="additionalContent">Zusätzlicher Inhalt (HTML-formatiert)</Label>
              <span className="text-xs text-muted-foreground">
                Unterstützt: Absätze, Listen, Fett, Kursiv
              </span>
            </div>
            <Textarea
              id="additionalContent"
              {...register('additionalContent')}
              placeholder="<p>Hier können Sie größere Textmengen per Copy & Paste einfügen.</p>&#10;<p>HTML-Formatierung wird automatisch übernommen:</p>&#10;<ul>&#10;  <li>Listen</li>&#10;  <li>Absätze</li>&#10;</ul>"
              rows={8}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Tipp: Kopieren Sie Text aus Word, Google Docs oder anderen Programmen - die Formatierung wird automatisch erkannt.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Abbrechen
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={handleSubmit((data) => saveOffer(data, false))}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Nur speichern
                </>
              )}
            </Button>
            <Button
              type="submit"
              disabled={isGenerating || isSaving}
              className="bg-cyan-600 hover:bg-cyan-700"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Erstelle PDF...
                </>
              ) : (
                <>
                  <FileDown className="w-5 h-5 mr-2" />
                  Speichern & PDF exportieren
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
